import _ from 'lodash'
import * as THREE from 'three'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'
import EditorDatumUtils from './../utils/editorDatumUtils'
import EditorUtils from '../utils/editorUtils'
import EditorFactory from '../utils/editorFactory'
import BaseObject from '../objects/baseObject'
import PointObject from '../objects/pointObject'
import GlassShape from '../model/glassShape'
import LineObject from '../library/three/LineObject'
import DatumLink from './datumLink'
import GlassSegment from '@/model/glassSegment'

import GlassDesignerTypes from '../types'

/*
 * GlassBaseHole
 * A hole object in the GlassPiece.
 * Holes are position via a datum point
 * Different hole types/shapes are classes that extend from this class
 */
export default class GlassBaseHole extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)
    this.objectClassName = 'GlassBaseHole'

    this.name = 'Center Cutout'
    this.holeType = GlassDesignerTypes.LibraryItemShapeTypes.rectangle
    this.worldPoint = new THREE.Vector2(0, 0)
    this.anchorPoint = new THREE.Vector2(0, 0)
    this.datumLinks = []

    this.isOpenShape = false      // Custom shapes may be open (eg, cutout path) and should be clockwise. Internal holes will be counter-clockwise
    this.listenMouseEvents = true

    // Angle in degrees
    // Used for rectangle center cutouts only
    this.angle = parseFloat(options.angle || 0)

    this.isCutoutPolished = !!options.isCutoutPolished
    this.isCountersunk = !!options.isCountersunk
    this.countersunkRadius = MathsUtils.roundNumber(parseFloat(options.countersunkRadius || 0), 1)

    // Anchor measure to types
    this.measureToHorizontal = GlassDesignerTypes.HoleMeasurePointTypes.left
    this.measureToVertical = GlassDesignerTypes.HoleMeasurePointTypes.top

    // Selection shape info
    this.selectionObj = null

    // An array of GlassSegment that build up the hole shape
    // GlassSegment's contain all the edge details. Other info is ignored for holes
    this.segmentList = []

    // Cached full point list generated from the segments
    // Used for point-in-poly testing. Cached for performance
    this.cachedPointList = []
    this.cachedBounds = new THREE.Box2()

    this.sceneObject = new THREE.Object3D()
    this.editor.scene.add(this.sceneObject)

    // Datum display point
    this.datumDisplayPointObj = new PointObject(editor, {
      colour: this.editor.getTheme().cutoutCenterDatumColour,
      opacity: this.editor.getTheme().cutoutCenterDatumOpacity
    })
    this.addChildObject(this.datumDisplayPointObj)
  }


  dispose() {
    super.dispose()

    if(this.selectionObj) {
      this.selectionObj.dispose()
      this.selectionObj = null
    }
    if(this.datumDisplayPointObj) {
      this.datumDisplayPointObj.dispose()
      this.datumDisplayPointObj = null
    }
  }


  /**
   * onBeforeRender
   * Called prior to rendering of the object
   */
  onBeforeRender() {
    super.onBeforeRender()

    // Selected support
    if(this.selectionObj) {
      this.selectionObj.updateResolution(this.editor.settings.viewportWidth, this.editor.settings.viewportHeight)
      this.selectionObj.setVisible(this.selected)
    }
  }


  /**
   * Update the view level on the object
   * Adjusts the scene object matrix & visibility for editing / viewing modes
   */
  viewLevelUpdated(viewLevel) {
    let displayHoleObjects = (viewLevel == GlassDesignerTypes.EditorViewLevelTypes.normal || viewLevel == GlassDesignerTypes.EditorViewLevelTypes.technical)

    // Only show hole info for normal view
    // The outline/hole display is handled via GlassShape
    if(this.datumDisplayPointObj)
      this.datumDisplayPointObj.setVisible(displayHoleObjects)
  }


  /**
   * Generate the THREE.Shape object based on the hole segment list
   */
  generateShape() {
    return MathsUtils.generateShapeFromSegmentList(this.segmentList, { isHole: !this.isOpenShape, isOpenShape: this.isOpenShape })
  }


  /**
   * Set the list of datum links
   * Typically links to 2 other objects (segments in the shape and/or holes)
   * Causes a rebuild of the hole's segment data
   * 
   */
  setDatumLinks(datumLinks) {
    this.datumLinks = datumLinks.map(datum => datum.clone())
    this.rebuildHoleShape()
  }


  /**
   * Check if any of the datum links are flagged as invalid
   */
  hasInvalidDatumLink() {
    return this.datumLinks.filter(datum => !datum.isValidDatumLink).length > 0 || this.datumLinks.length == 0
  }


  /**
   * Update the center cutout settings
   */
  updateCutoutSettings(info) {
    if(info.angle !== undefined)
      this.angle = parseFloat(info.angle || 0)
    if(info.isCutoutPolished !== undefined)
      this.isCutoutPolished = !!info.isCutoutPolished
    if(info.isCountersunk !== undefined)
      this.isCountersunk = !!info.isCountersunk
    if(info.countersunkRadius !== undefined)
      this.countersunkRadius = parseFloat(info.countersunkRadius || 0)
    if(info.measureToHorizontal !== undefined)
      this.measureToHorizontal = info.measureToHorizontal
    if(info.measureToVertical !== undefined)
      this.measureToVertical = info.measureToVertical

    this.holeSizeUpdated()
    this.rebuildHoleShape()
  }


  /**
   * Get a datum link for the hole given an ID
   * Returns null if no datum link found
   */
  getDatumLinkById(id) {
    return this.datumLinks.find(link => link.id == id)
  }


  /**
   * Calculate the world position the datum links resolve to
   * Requires 2 datum points set
   */
  updateDatumLinkWorldPoint(datumOptions = {}) {
    let point = EditorDatumUtils.calculateDatumLinksWorldPoint(this.editor, this.datumLinks, datumOptions)

    // Don't update if invalid. Datum links will be flagged invalid internally
    // Other functions will handle resetting the datum links if required
    if(point)
      this.worldPoint.copy(point)
  }


  /**
   * Hole sizing was updated
   * Recalculates the shape and then the anchor point based on the shape
   */
  holeSizeUpdated() {
    this.rebuildHoleShape()

    // Force update on anchor details
    // Will cause a rebuild on the anchor offset points
    this.setAnchorInfo(this.measureToHorizontal, this.measureToVertical)
  }


  /**
   * Update the anchor point based on the measure to types
   */
  setAnchorInfo(offsetHorizontalType, offsetVerticalType) {
    this.measureToHorizontal = offsetHorizontalType
    this.measureToVertical = offsetVerticalType

    // Calculate the point based on segment bounds
    // Note: Anchor points are relative to the hole
    let bounds = this.calculateSegmentBounds()
    let offsetPoint = MathsUtils.calculateHoleOffsetPoint(bounds, this.measureToHorizontal, this.measureToVertical)

    this.anchorPoint.x = offsetPoint.x
    this.anchorPoint.y = offsetPoint.y

    this.rebuildHoleShape()
  }


  /**
   * Calculate the anchor point in world coordinates
   */
  calculateAnchorWorldPoint() {
    let bounds = this.calculateSegmentBounds()

    return new THREE.Vector2(bounds.min.x + this.anchorPoint.x, bounds.min.y + this.anchorPoint.y)
  }


  /**
   * Rotate the hole
   * Overriden by the shape classes
   */
  rotateHole(angleDegrees) {
    // Note: This process only works for 90 degree rotations. Which it currently is

    // Rotate around the measure to points
    var cornerList = [
      [GlassDesignerTypes.HoleMeasurePointTypes.left, GlassDesignerTypes.HoleMeasurePointTypes.top],
      [GlassDesignerTypes.HoleMeasurePointTypes.right, GlassDesignerTypes.HoleMeasurePointTypes.top],
      [GlassDesignerTypes.HoleMeasurePointTypes.right, GlassDesignerTypes.HoleMeasurePointTypes.bottom],
      [GlassDesignerTypes.HoleMeasurePointTypes.left, GlassDesignerTypes.HoleMeasurePointTypes.bottom],
    ]
    var midList = [
      [GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.top],
      [GlassDesignerTypes.HoleMeasurePointTypes.right, GlassDesignerTypes.HoleMeasurePointTypes.middle],
      [GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.bottom],
      [GlassDesignerTypes.HoleMeasurePointTypes.left, GlassDesignerTypes.HoleMeasurePointTypes.middle]
    ]

    var rotateHorz = this.measureToHorizontal
    var rotateVert = this.measureToVertical

    cornerList.forEach((cornerInfo, index) => {
      if(this.measureToHorizontal == cornerInfo[0] && this.measureToVertical == cornerInfo[1]) {
        rotateHorz = cornerList[(index+1) % cornerList.length][0]
        rotateVert = cornerList[(index+1) % cornerList.length][1]
      }
    })
    midList.forEach((cornerInfo, index) => {
      if(this.measureToHorizontal == cornerInfo[0] && this.measureToVertical == cornerInfo[1]) {
        rotateHorz = midList[(index+1) % midList.length][0]
        rotateVert = midList[(index+1) % midList.length][1]
      }
    })

    this.rebuildHoleShape()
    this.setAnchorInfo(rotateHorz, rotateVert)
  }


  /**
   * Rebuild the hole shape
   * Overriden by the shape classes
   */
  rebuildHoleShape() {
  }


  /**
   * Update the selection display object from the hole shape segments
   * Called when rebuilding the hole shape
   */
  updateSceneObject() {
    if(!this.sceneObject)
      return

    if(this.selectionObj) {
      this.selectionObj.dispose()
      this.selectionObj = null
    }

    this.clearSceneObject()

    var segPoints = this.generateShape().getPoints(Config.Settings.arcSubdivisions)

    // Updated cached point list
    // Use less subdivisions for performance
    // Hole set to false as we want the inside area (for point in poly detection)
    this.cachedPointList = MathsUtils.generateShapeFromSegmentList(this.segmentList, { isHole: false }).getPoints(10)

    this.cachedBounds.makeEmpty()
    segPoints.forEach(pnt => {
      this.cachedBounds.expandByPoint(pnt)
    })


    this.selectionObj = new LineObject({
      colour: this.editor.getTheme().selectionColour,
      width: this.editor.getTheme().selectionWidth
    })
    this.selectionObj.updateLinePath(segPoints)
    if(this.selectionObj.getThreeObject())
      this.sceneObject.add(this.selectionObj.getThreeObject())
    
    this.selectionObj.setVisible(this.selected)
  }

  clearSelection() {
    super.clearSelection()
  }


  setSelected(selectionData, isSelected) {
    super.setSelected(selectionData, isSelected)
    
    if(this.selectionObj)
      this.selectionObj.setVisible(this.selected)
  }


  /**
   * Calculate the 2D bounds of the glass hole based on segments only
   * Returns a Box2 object with min/max values for X/Y coordinates
   */
  calculateSegmentBounds() {
    return MathsUtils.calculateGlassSegmentListBounds(this.segmentList)
  }


  /**
   * Calculate the area of the glass hole
   * Returns a value in millimetres squared
   */
  calculateArea() {
    var area = 0
    let shape = MathsUtils.generateShapeFromSegmentList(this.segmentList, { isHole: !this.isOpenShape })
    let pointList = shape.getPoints(Config.Settings.arcSubdivisions)

    pointList.forEach((point, index) => {
      let prevIndex = index == 0 ? pointList.length-1 : index-1
      area += (pointList[prevIndex].x + point.x) * (pointList[prevIndex].y - point.y)
    })

    return Math.abs(area / 2.0)
  }


  /**
   * Checks if a 2D circle intersects with the hole
   * Performs a single bounds check unless overridden by a hole with a specific shape (eg, hole)
   * Returns true if an intersection occurred & sets the closest point on the hole the point is to
   */
  check2DCircleIntersection(point, radius, outIntPoint) {
    // Overridden by the specific shapes
    return false
  }


  /**
   * Check if this hole intersects with other holes
   * Returns true if intersections exist
   */
  intersectsOtherHoles(holeList) {
    return holeList.filter(otherHole => {
      // Ignore self
      if(otherHole.id == this.id)
        return false

      // Bounds early exit test
      if(!this.cachedBounds.isEmpty() && !otherHole.cachedBounds.isEmpty()) {
        if(!this.cachedBounds.intersectsBox(otherHole.cachedBounds) && !(this.cachedBounds.containsBox(otherHole.cachedBounds) || otherHole.cachedBounds.containsBox(this.cachedBounds)))
          return false
      }

      if(otherHole.segmentList.find(holeSegment => {
        return this.checkGlassSegmentIntersection(holeSegment)
      }) != null)
        return true

      return false

    }).length > 0
  }


  /**
   * Check if GlassSegment object intersects with this hole segments
   * Returns a Vector2 of the first intersection point found
   * Returns null if no intersection occured
   */
  checkGlassSegmentIntersection(segment) {
    for(var holeSegment of this.segmentList) {
      let intersectionList = holeSegment.calculateGlassSegmentIntersection(segment)
      if(intersectionList.length > 0)
        return intersectionList[0]
    }

    return null
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    // Ignore if not visible
    if(!this.isVisible())
      return null

    var closestIntersection = null
    let radiusTest = Math.max(worldRadius, 0.1)

    // Create a plane from the glass shape
    // Assumes the 2D shape is sitting on the plane at origin (Z = 0)
    var shapePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(shapePlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)
    var intPoint = new THREE.Vector2()

    if(this.check2DCircleIntersection(ray2DPoint, radiusTest, intPoint)) {
      let dist = ray2DPoint.distanceTo(intPoint)

      closestIntersection = {
        intersection: intPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: dist,
        layer: GlassDesignerTypes.EditorUILayers.selection
      }
    }

    return closestIntersection
  }


  /**
   * Clone the hole
   */
  clone() {    
    var newHole = EditorFactory.createObject(this.objectClassName, this.editor)

    newHole.copy(this)

    return newHole
  }


  /**
   * Copy details from a hole to this hole
   */
  copy(otherHole) {
    super.copy(otherHole)

    // Note: Datum links will clone the IDs & may require adjusting by code using the clone
    
    this.name = otherHole.name
    this.holeType = otherHole.holeType
    this.anchorPoint = otherHole.anchorPoint.clone()
    this.datumLinks = otherHole.datumLinks.map(d => d.clone())
    this.angle = otherHole.angle

    this.isCutoutPolished = otherHole.isCutoutPolished
    this.isCountersunk = otherHole.isCountersunk
    this.countersunkRadius = otherHole.countersunkRadius

    this.measureToHorizontal = otherHole.measureToHorizontal
    this.measureToVertical = otherHole.measureToVertical

    this.segmentList = otherHole.segmentList.map(s => s.clone())
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {
    var objData = super.serialize()

    objData.name = this.name
    objData.holeType = this.holeType
    objData.anchorPoint = MathsUtils.serializeVector2(this.anchorPoint)
    objData.worldPoint = MathsUtils.serializeVector2(this.worldPoint)
    objData.datumLinks = this.datumLinks.map(datum => datum.serialize())
    objData.angle = this.angle
    objData.isCutoutPolished = this.isCutoutPolished
    objData.isCountersunk = this.isCountersunk
    objData.countersunkRadius = this.countersunkRadius

    objData.measureToHorizontal = this.measureToHorizontal
    objData.measureToVertical = this.measureToVertical

    objData.segmentList = this.segmentList.map(segment => segment.serialize())

    return objData
  }
  deserialize(object) {
    super.deserialize(object)

    this.name = object.name
    this.holeType = object.holeType
    this.anchorPoint = MathsUtils.deserializeVector2(object.anchorPoint)
    this.worldPoint = MathsUtils.deserializeVector2(object.worldPoint)
    this.datumLinks = (object.datumLinks || []).map(datumData => {
      var datum = new DatumLink()
      datum.deserialize(datumData)
      return datum
    })
    this.angle = parseFloat(object.angle || 0)
    this.isCutoutPolished = !!object.isCutoutPolished
    this.isCountersunk = !!object.isCountersunk
    this.countersunkRadius = parseFloat(object.countersunkRadius || 0)

    this.measureToHorizontal = object.measureToHorizontal
    this.measureToVertical = object.measureToVertical

    this.segmentList = (object.segmentList || []).map(segmentData => {
      var segment = new GlassSegment(segmentData.type)
      segment.deserialize(segmentData)
      return segment
    })
  }
}